import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { filter, switchMap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { LanguageModel } from '@app/shared/dialog/select-lang/language.model';
import { AuthService } from '@app/core/auth/auth.service';
import { UserService } from '@app/core/auth/user.service';
import { TenantService } from '@app/core/services/tenant.service';
import { TenantModel } from '@app/core/models/tenant.model';

@Injectable({ providedIn: 'root' })
export class InspektTranslateService {
    private translate = inject(TranslateService);
    authService = inject(AuthService);
    userService = inject(UserService);
    tenantService = inject(TenantService);


    isProd = environment.production;
    private readonly destroyRef = inject(DestroyRef);

    public supportedLanguages = [
        {
            id: 0,
            code: 'en',
            name: 'English',
            default: false
        },
        {
            id: 1,
            code: 'zh-hans', // chinese - simplest
            name: '簡體中文',
            default: false
        },
        {
            id: 2,
            code: 'zh-hant', // chinese - traditional
            name: '中國傳統的',
            default: false
        },
        {
            id: 3,
            code: 'vi',
            name: 'Tiếng Việt',
            default: false
        }
    ] as LanguageModel[];

    public useTranslate() {
        this.translate.setDefaultLang('en');

        // TODO: Current force set language use English in Production.
        if (this.isProd) {
            this.use('en');
            return;
        }

        // When the user finishes authenticating, select the language based on the tenant config.
        // switchMap cancels any in-flight tenantService.get() if auth state changes again (CS-7.3).
        // takeUntilDestroyed ensures the subscription is cleaned up when the service is destroyed.
        this.authService.isAuthenticatedSubj.pipe(
            filter(isAuthenticated => !!isAuthenticated && !!this.userService.currentUser?.tenantId),
            switchMap(() => this.tenantService.get(this.userService.currentUser.tenantId)),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((tenant: TenantModel) => {
            if (tenant) {
                const defaultLang = this.supportedLanguages.find(t => t.code === tenant.defaultLanguage);
                if (defaultLang) {
                    this.use(tenant.defaultLanguage);
                    defaultLang.default = true;
                }
            }
        });
    }

    public get(key: string | string[], params?: Object) {
        return this.translate.get(key, params);
    }

    public instant(key: string | string[], params?: Object) {
        return this.translate.instant(key, params);
    }

    public use(lang: string) {
        this.translate.use(lang);
    }

    public useWithId(id: number) {
        const lang = this.supportedLanguages.find(t => t.id === id);
        if (lang) {
            this.use(lang.code);
        } else {
            this.use('en');
        }
    }

    public getDefaultLanguage() {
        return this.supportedLanguages.find(t => t.default);
    }
}
